(() => {
  'use strict';

  const sections = document.querySelectorAll('.c_store-search');
  if (!sections.length) return;

  /* -----------------------------------------------------------
   * セクション初期化
   *
   * 店舗データは全て記事メタフィールド `store_search.*` から Liquid 側で
   * data-* 属性に出力済み（日本語値→slugへの変換も snippets/c_store-search-slug.liquid で実施済み）。
   * このスクリプトの責務はフィルタUI操作・キーワード検索・もっと見る等のクライアント操作のみ。
   *
   * ただし blog.articles は paginate 1ページあたり 250 件が上限のため、
   * 251 件目以降は Section Rendering API で取得してリストに結合する。
   */
  sections.forEach(initSection);

  function initSection(section) {
    const state = {
      region: 'all',
      prefecture: '',
      country: 'all',
      brands: new Set(),
      services: new Set(),
      keyword: '',
    };

    const controller = new AbortController();
    const { signal } = controller;

    const updatePrefectureVisibility = initialisePrefectures(section, state, signal);
    initialiseSingleSelect(section, 'region', state, signal, () => {
      if (state.region !== 'all') resetSingleSelect(section, 'country', state);
      if (typeof updatePrefectureVisibility === 'function') updatePrefectureVisibility();
    });
    initialiseSingleSelect(section, 'country', state, signal, () => {
      if (state.country !== 'all') {
        resetSingleSelect(section, 'region', state);
        if (typeof updatePrefectureVisibility === 'function') updatePrefectureVisibility();
      }
    });
    initialiseMultiSelect(section, 'brand', state, 'brands', signal);
    initialiseMultiSelect(section, 'service', state, 'services', signal);
    initialiseKeywordInput(section, state, signal);
    initialiseLoadMore(section, state, signal);

    if (updatePrefectureVisibility) updatePrefectureVisibility();

    /*  1ページ目のカードだけで先に描画し、操作可能な状態にしておく。
        残りのページは取得後に再度フィルタをかけて反映する。 */
    applyFilters(section, state);

    loadRemainingStorePages(section, signal).then((added) => {
      if (added > 0) applyFilters(section, state);
    });

    document.addEventListener(
      'shopify:section:unload',
      (e) => {
        if (e.target === section || e.target.contains(section)) {
          controller.abort();
        }
      },
      { signal }
    );
  }

  /* -----------------------------------------------------------
   * 251件目以降の取得
   *
   * Liquid 側の <ul data-store-search-list> が持つ data 属性を使う。
   *   data-total-pages    : paginate.pages（総ページ数）
   *   data-page-base-url  : blog.url（Section Rendering API のベースURL）
   * 返却されるのはセクション全体の HTML のため、カードの li だけを取り出して追加する。
   */
  async function loadRemainingStorePages(section, signal) {
    const list = section.querySelector('[data-store-search-list]');
    if (!list) return 0;

    const totalPages = Number(list.dataset.totalPages || 1);
    const baseUrl = list.dataset.pageBaseUrl || '';
    const sectionId = section.dataset.sectionId || '';
    if (!Number.isFinite(totalPages) || totalPages <= 1 || !baseUrl || !sectionId) return 0;

    let added = 0;

    for (let page = 2; page <= totalPages; page++) {
      if (signal.aborted) break;

      try {
        const url = `${baseUrl}?section_id=${encodeURIComponent(sectionId)}&page=${page}`;
        const response = await fetch(url, { signal });
        if (!response.ok) continue;

        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const items = doc.querySelectorAll('[data-store-search-list] > [data-store-card]');

        items.forEach((item) => {
          list.appendChild(document.importNode(item, true));
          added += 1;
        });
      } catch (error) {
        /*  1ページ失敗しても残りの取得は続ける（取得済みの分は表示したまま） */
        if (error.name === 'AbortError') break;
        console.warn('[store-search] 店舗一覧の追加取得に失敗しました', error);
      }
    }

    return added;
  }

  /* -----------------------------------------------------------
   * フィルタ操作
   */
  function resetSingleSelect(section, group, state) {
    const groupRoot = section.querySelector(`[data-filter-group="${group}"]`);
    if (!groupRoot) return;
    const buttons = groupRoot.querySelectorAll(':scope > .c_store-search__chip-list [data-filter-value]');
    if (!buttons.length) return;
    state[group] = 'all';
    buttons.forEach((b) => {
      const isAll = b.dataset.filterValue === 'all';
      b.classList.toggle('is-active', isAll);
      b.setAttribute('aria-pressed', isAll ? 'true' : 'false');
    });
  }

  function initialiseSingleSelect(section, group, state, signal, onChange) {
    const groupRoot = section.querySelector(`[data-filter-group="${group}"]`);
    if (!groupRoot) return;
    const buttons = groupRoot.querySelectorAll(':scope > .c_store-search__chip-list [data-filter-value]');
    if (!buttons.length) return;

    const active = groupRoot.querySelector(':scope > .c_store-search__chip-list .is-active[data-filter-value]');
    if (active) state[group] = active.dataset.filterValue || 'all';

    buttons.forEach((btn) => {
      btn.addEventListener(
        'click',
        () => {
          const value = btn.dataset.filterValue || 'all';
          state[group] = value;
          buttons.forEach((b) => {
            const isMe = b === btn;
            b.classList.toggle('is-active', isMe);
            b.setAttribute('aria-pressed', isMe ? 'true' : 'false');
          });
          if (typeof onChange === 'function') onChange();
          applyFilters(section, state);
        },
        { signal }
      );
    });
  }

  function initialisePrefectures(section, state, signal) {
    const list = section.querySelector('[data-store-search-prefecture-list]');
    if (!list) return null;
    const buttons = list.querySelectorAll('[data-prefecture-value]');
    if (!buttons.length) return null;

    buttons.forEach((btn) => {
      btn.addEventListener(
        'click',
        () => {
          const value = btn.dataset.prefectureValue || '';
          const isActive = btn.classList.contains('is-active');
          buttons.forEach((b) => {
            b.classList.remove('is-active');
            b.setAttribute('aria-pressed', 'false');
          });
          if (isActive) {
            state.prefecture = '';
          } else {
            btn.classList.add('is-active');
            btn.setAttribute('aria-pressed', 'true');
            state.prefecture = value;
          }
          applyFilters(section, state);
        },
        { signal }
      );
    });

    return function updateVisibility() {
      let hasVisible = false;
      const region = state.region;
      buttons.forEach((btn) => {
        const parent = btn.dataset.parentRegion || '';
        const visible = region && region !== 'all' && parent === region;
        btn.hidden = !visible;
        if (visible) hasVisible = true;
        if (!visible && btn.classList.contains('is-active')) {
          btn.classList.remove('is-active');
          btn.setAttribute('aria-pressed', 'false');
          state.prefecture = '';
        }
      });
      list.hidden = !hasVisible;
    };
  }

  function initialiseMultiSelect(section, group, state, key, signal) {
    const buttons = section.querySelectorAll(`[data-filter-group="${group}"] [data-filter-value]`);
    if (!buttons.length) return;

    buttons.forEach((btn) => {
      btn.addEventListener(
        'click',
        () => {
          const value = btn.dataset.filterValue || '';
          const pressed = btn.getAttribute('aria-pressed') === 'true';
          const next = !pressed;
          btn.setAttribute('aria-pressed', next ? 'true' : 'false');
          btn.classList.toggle('is-active', next);
          if (next) {
            state[key].add(value);
          } else {
            state[key].delete(value);
          }
          applyFilters(section, state);
        },
        { signal }
      );
    });
  }

  function initialiseKeywordInput(section, state, signal) {
    const input = section.querySelector('[data-store-search-input]');
    if (!input) return;
    input.addEventListener(
      'input',
      debounce(() => {
        state.keyword = input.value.trim().toLowerCase();
        applyFilters(section, state);
      }, 120),
      { signal }
    );
  }

  function initialiseLoadMore(section, state, signal) {
    const button = section.querySelector('[data-store-search-load-more]');
    if (!button) return;
    button.addEventListener(
      'click',
      () => {
        const expanded = button.getAttribute('aria-expanded') === 'true';
        button.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        section.classList.toggle('is-expanded', !expanded);
        applyFilters(section, state);
      },
      { signal }
    );
  }

  function applyFilters(section, state) {
    const isExpanded = section.classList.contains('is-expanded');
    const initialVisible = parseInt(section.dataset.initialVisible || '4', 10);

    const cards = section.querySelectorAll('[data-store-card]');
    let matched = 0;
    let shown = 0;

    cards.forEach((card) => {
      const ok = matchCard(card, state);
      if (!ok) {
        card.hidden = true;
        return;
      }
      matched += 1;
      if (!isExpanded && shown >= initialVisible) {
        card.hidden = true;
        return;
      }
      card.hidden = false;
      shown += 1;
    });

    updateCount(section, matched);
    updateEmptyMessage(section, matched);
    updateLoadMoreVisibility(section, matched, initialVisible);
  }

  function updateEmptyMessage(section, matched) {
    const empty = section.querySelector('[data-store-search-empty]');
    if (!empty) return;
    empty.hidden = matched > 0;
  }

  function matchCard(card, state) {
    if (state.country && state.country !== 'all') {
      if ((card.dataset.country || '') !== state.country) return false;
    } else if (state.region && state.region !== 'all') {
      if ((card.dataset.region || '') !== state.region) return false;
    }
    if (state.prefecture) {
      if ((card.dataset.prefecture || '') !== state.prefecture) return false;
    }
    if (state.brands.size > 0) {
      const brands = splitMulti(card.dataset.storeBrands);
      const ok = [...state.brands].every((v) => brands.includes(v));
      if (!ok) return false;
    }
    if (state.services.size > 0) {
      const services = splitMulti(card.dataset.storeServices);
      const ok = [...state.services].every((v) => services.includes(v));
      if (!ok) return false;
    }
    if (state.keyword) {
      const text = (card.dataset.searchText || '').toLowerCase();
      if (!text.includes(state.keyword)) return false;
    }
    return true;
  }

  function splitMulti(raw) {
    return String(raw || '').split('|').map((s) => s.trim()).filter(Boolean);
  }

  function updateCount(section, count) {
    const counter = section.querySelector('[data-store-search-count]');
    if (!counter) return;
    const template = counter.dataset.template || '';
    counter.textContent = template.replace('{count}', count);
  }

  function updateLoadMoreVisibility(section, matched, initialVisible) {
    const button = section.querySelector('[data-store-search-load-more]');
    if (!button) return;
    if (matched > initialVisible) {
      button.removeAttribute('data-hidden');
    } else {
      button.setAttribute('data-hidden', 'true');
    }
  }

  function debounce(fn, wait) {
    let id;
    return (...args) => {
      clearTimeout(id);
      id = setTimeout(() => fn.apply(null, args), wait);
    };
  }
})();